// Apolo IDE — CodeSidebar.
//
// Search + New session button + Pinned + Recents + Account footer. Minimal
// Epic 4 implementation: secondary actions (Routines, Customize, Mais) are
// rendered as buttons that emit DOM events for future epics to wire.

import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, ChevronLeft, ChevronRight, Folder, FolderPlus, GitBranch, Home, Pin, PinOff, Plus, Search, Trash2, PlugZap, LayoutGrid, LayoutDashboard, List as ListIcon, Columns3, Wand2 } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { useSidebarPins } from '../../hooks/useSidebarPins.js';
import { sessionKind } from '../../lib/code/sessionKind.js';
import { filterSessionsForSurface, groupSessionsByFolder } from '../../lib/code/sessionSidebarModel.js';
import { evictRun } from '../../lib/agentRunStore.js';
import ScheduleModal from '../ScheduleModal.jsx';
import PluginsView from '../PluginsView.jsx';
import IntegrationsView from '../IntegrationsView.jsx';
import FolderRow from './FolderRow.jsx';
import SidebarNavItem from './SidebarNavItem.jsx';
import SessionStatusBadge from './SessionStatusBadge.jsx';
import CommandCenterModal from './CommandCenterModal.jsx';
import { useSessionStatusPoll } from '../../hooks/useSessionStatusPoll.js';
import { filterSessionsByStatus, groupSessionsByStatus, groupSessionsByProject, groupSessionsByTime } from '../../lib/code/sessionGrouping.js';
import { relativeDate, fullDate } from '../../lib/code/sessionMeta.js';
import { pluginColor } from '../../lib/pluginColor.js';
import { sessionStatusMeta } from '../../lib/code/sessionStatusMeta.js';
import { modKey } from '../../lib/keyboardUtils.js';
import {
  isSessionDragEvent,
  readSessionIdFromDataTransfer,
  setSessionDragData,
} from '../../lib/sessionDrag.js';
import styles from './CodeSidebar.module.css';

// Sprint v9.18 — sidebar retrátil. Detecta desktop via media query;
// mobile (drawer fullscreen) ignora collapsed e sempre renderiza expanded.
const LS_COLLAPSED = 'apolo:sidebar:collapsed';
function readCollapsed() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LS_COLLAPSED) === '1';
  } catch {
    return false;
  }
}

// Largura arrastável (desktop) — persistida. Clamp p/ não sumir nem engolir o canvas.
const LS_WIDTH = 'apolo:sidebar:width';
const SIDEBAR_MIN = 220;
const SIDEBAR_MAX = 480;
const SIDEBAR_DEFAULT = 288; // = w-72
function clampWidth(px) {
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(px)));
}
function readWidth() {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT;
  try {
    const raw = Number(window.localStorage.getItem(LS_WIDTH));
    return Number.isFinite(raw) && raw > 0 ? clampWidth(raw) : SIDEBAR_DEFAULT;
  } catch {
    return SIDEBAR_DEFAULT;
  }
}

export default function CodeSidebar({ onNewSession }) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [pluginsOpen, setPluginsOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  // Command Center — visão macro de todas as sessões (⌘⇧O). Estado local;
  // os outros modais aqui seguem o mesmo padrão.
  const [showCommandCenter, setShowCommandCenter] = useState(false);
  // CodeView top-level screen ('home' | 'schedules' | 'sprints' | 'codemap').
  // Home used to light up whenever activeSessionId was null — that wrongly
  // highlighted Home while Agenda/Kanban were open.
  const [codeView, setCodeView] = useState('home');
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const [collapsedStored, setCollapsedStored] = useState(readCollapsed);
  // Em mobile (drawer fullscreen) ignora collapsed — sempre expanded.
  const collapsed = isDesktop && collapsedStored;

  // Largura arrastável (desktop) — persistida em localStorage. `resizing` desliga
  // a transição de width durante o drag pra a barra colar no ponteiro (sem lag).
  const [sidebarWidth, setSidebarWidth] = useState(readWidth);
  const [resizing, setResizing] = useState(false);
  useEffect(() => {
    try { window.localStorage.setItem(LS_WIDTH, String(sidebarWidth)); } catch { /* ignore */ }
  }, [sidebarWidth]);

  function startResize(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidth;
    setResizing(true);
    const onMove = (ev) => setSidebarWidth(clampWidth(startW + (ev.clientX - startX)));
    const onUp = () => {
      setResizing(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_COLLAPSED, collapsedStored ? '1' : '0');
    } catch {
      // ignore
    }
    // Avisa o UserStatusBar (flutuante global) pra recolher junto com o sidebar.
    window.dispatchEvent(
      new CustomEvent('apolo:sidebarCollapsed', { detail: { collapsed: collapsedStored } }),
    );
  }, [collapsedStored]);

  // Listen to global shortcut events from useGlobalShortcuts.
  // ⌘N opens new session, ⇧⌘T opens schedule, ⇧⌘K opens plugins,
  // ⇧⌘O opens the Command Center.
  useEffect(() => {
    const openSchedule = () => setScheduleOpen(true);
    const openPlugins = () => setPluginsOpen(true);
    const newSession = () => onNewSession?.();
    const openCommandCenter = () => setShowCommandCenter(true);
    const onSetCodeView = (e) => {
      const v = e?.detail?.view;
      if (v === 'home' || v === 'schedules' || v === 'sprints' || v === 'codemap') {
        setCodeView(v);
      }
    };
    window.addEventListener('apolo:openSchedule', openSchedule);
    window.addEventListener('apolo:openPlugins', openPlugins);
    window.addEventListener('apolo:newSession', newSession);
    window.addEventListener('apolo:openCommandCenter', openCommandCenter);
    window.addEventListener('apolo:setCodeView', onSetCodeView);
    return () => {
      window.removeEventListener('apolo:openSchedule', openSchedule);
      window.removeEventListener('apolo:openPlugins', openPlugins);
      window.removeEventListener('apolo:newSession', newSession);
      window.removeEventListener('apolo:openCommandCenter', openCommandCenter);
      window.removeEventListener('apolo:setCodeView', onSetCodeView);
    };
  }, [onNewSession]);
  const sessions = useAgentStore((s) => s.code.sessions);
  const folders = useAgentStore((s) => s.code.folders);
  const activeId = useAgentStore((s) => s.code.activeSessionId);
  const search = useAgentStore((s) => s.code.sidebarSearch);
  const setSearch = useAgentStore((s) => s.setSidebarSearch);
  const setActive = useAgentStore((s) => s.setActiveSession);
  const refreshFolders = useAgentStore((s) => s.refreshFolders);
  const createFolder = useAgentStore((s) => s.createFolder);
  const moveSession = useAgentStore((s) => s.moveSessionToFolder);
  const deleteCodeSession = useAgentStore((s) => s.deleteCodeSession);
  const sessionStatuses = useAgentStore((s) => s.code.sessionStatuses);
  // Glow ao vivo: sessões streamando AGORA (client-only). Sobrepõe o status do
  // poll como 'running' (verde) na hora — ver useStreamingChat + setSessionStreaming.
  const streamingSessionIds = useAgentStore((s) => s.code.streamingSessionIds);
  const ui = useAgentStore((s) => s.code.ui);
  const setUiPref = useAgentStore((s) => s.setUiPref);
  const backfillSessionTitles = useAgentStore((s) => s.backfillSessionTitles);
  const [backfilling, setBackfilling] = useState(false);
  async function organizeNames() {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const r = await backfillSessionTitles();
      const msg = r?.ok
        ? (r.renamed > 0 ? `${r.renamed} sessão(ões) renomeada(s) pela 1ª mensagem` : 'Nenhum nome genérico pra organizar')
        : 'Não foi possível organizar os nomes';
      window.dispatchEvent(new CustomEvent('apolo:toast', { detail: { kind: r?.ok ? 'success' : 'error', message: msg } }));
    } finally {
      setBackfilling(false);
    }
  }
  const statusFilter = ui?.statusFilter || 'all';
  const sessionView = ['board', 'project', 'time'].includes(ui?.sessionView) ? ui.sessionView : 'list';

  // Poll do status das sessões enquanto a aba Code está ativa (limpa no unmount).
  useSessionStatusPoll();

  // Confirma + chama action do store. Stop propagation pra não disparar
  // setActive no click do row (acessível: foco fica no botão excluído).
  async function handleDeleteSession(e, session) {
    e.preventDefault();
    e.stopPropagation();
    const name = session.name || '(sem nome)';
    if (!window.confirm(`Excluir sessão "${name}"? Esta ação não pode ser desfeita.`)) return;
    await deleteCodeSession(session.id);
    // perf/RAM — drop the deleted session's resident run + per-session caches
    // so nothing lingers in memory after it's gone from D1.
    evictRun(session.id);
  }

  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [recentsDragOver, setRecentsDragOver] = useState(false);
  const [pinnedDragOver, setPinnedDragOver] = useState(false);
  const newFolderInputRef = useRef(null);

  // Initial load + focus management for the inline "+ Nova pasta" input.
  useEffect(() => {
    refreshFolders();
  }, [refreshFolders]);

  useEffect(() => {
    if (newFolderMode && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [newFolderMode]);

  async function commitNewFolder() {
    const name = newFolderName.trim();
    if (!name) {
      setNewFolderMode(false);
      setNewFolderName('');
      return;
    }
    await createFolder(name);
    setNewFolderName('');
    setNewFolderMode(false);
  }

  const filtered = useMemo(() => {
    return filterSessionsForSurface(sessions, { surface: 'code', search });
  }, [sessions, search]);

  const sessionsByFolder = useMemo(() => {
    return groupSessionsByFolder(filtered);
  }, [filtered]);

  // (Pedro 2026-06-25, sprint cursor-strip) — pins agora vêm de LS via hook,
  // não do bd. O `s.pinned` continua aceito como fallback (futura migration).
  const { isPinned, togglePin, unpin } = useSidebarPins();
  const isSessionPinned = (s) => !!s?.id && (isPinned(s.id) || !!s.pinned);
  const pinned = filtered.filter(isSessionPinned);
  const ungrouped = (sessionsByFolder.get('__root__') || []).filter((s) => !isSessionPinned(s));

  // Filtro por status (chips) aplicado às sessões da raiz.
  const visibleUngrouped = useMemo(
    () => filterSessionsByStatus(ungrouped, sessionStatuses, statusFilter),
    [ungrouped, sessionStatuses, statusFilter],
  );
  // Visão board: agrupa por status (atenção primeiro). Respeita o filtro ativo.
  const recentGroups = useMemo(
    () => groupSessionsByStatus(visibleUngrouped, sessionStatuses),
    [visibleUngrouped, sessionStatuses],
  );
  // Visão projeto (Constelações): agrupa pela pasta/repo, cor por projeto.
  const projectGroups = useMemo(() => groupSessionsByProject(visibleUngrouped), [visibleUngrouped]);
  // Visão tempo (estilo claude): Hoje / Ontem / Esta semana / …
  const timeGroups = useMemo(() => groupSessionsByTime(visibleUngrouped), [visibleUngrouped]);

  // Linha de sessão reusada por lista e board.
  // (Pedro 2026-06-25, sprint cursor-strip) — tipo da sessão derivado do
  // provider do modelo: cloud (☁) ou local (▣). Coordena com a strip do
  // composer pra Pedro ler "onde isso roda" sem abrir nada. Silente se
  // o modelo não está mapeado (não inventa chrome). (E5) — agora via o
  // helper compartilhado sessionKind; Icon null = modelo não mapeado.
  const renderRow = (s) => {
    const kind = sessionKind(s?.model);
    const pinnedNow = isSessionPinned(s);
    return (
    <li key={s.id} className="group/recent relative">
      <button
        type="button"
        draggable
        title="Arraste para abrir em painel ao lado"
        onDragStart={(e) => {
          setSessionDragData(e.dataTransfer, s.id);
        }}
        onClick={() => setActive(s.id)}
        className={
          // pr-12 (não pr-7) — reserva espaço pros DOIS botões de hover:
          // pin (esq) + delete (dir).
          'flex w-full cursor-grab items-center gap-2 rounded-md py-1.5 pl-2 pr-12 text-left text-xs transition active:cursor-grabbing ' +
          styles.row +
          ' ' +
          (s.id === activeId
            ? 'bg-apolo-surface4 text-apolo-textPrimary'
            : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary')
        }
      >
        {/* (Pedro) the yellow status dots are replaced by the session's git
            branch (fork); only a LIVE-streaming run keeps the green badge. */}
        {streamingSessionIds?.[s.id] ? (
          <SessionStatusBadge status={{ status: 'running', reason: 'streamando agora' }} compact />
        ) : (
          <GitBranch
            size={12}
            className="shrink-0 text-apolo-textTertiary"
            aria-label={s.branch ? `Branch ${s.branch}` : 'Sessão'}
          />
        )}
        {kind.Icon ? (
          <kind.Icon
            size={11}
            className="shrink-0 text-apolo-textTertiary"
            aria-label={`Execução ${kind.label}`}
          />
        ) : null}
        <span className="min-w-0 flex-1 truncate">{s.name}</span>
        {s.branch && (
          <span
            className="ml-1 shrink-0 truncate font-mono text-[10px] text-apolo-textTertiary transition-opacity group-hover/recent:opacity-0"
            style={{ maxWidth: 64 }}
            title={s.branch}
          >
            {s.branch}
          </span>
        )}
        {(s.updated_at || s.created_at) && (
          <span
            className="ml-1 shrink-0 text-[10px] tabular-nums text-apolo-textTertiary transition-opacity group-hover/recent:opacity-0"
            title={fullDate(s.updated_at || s.created_at)}
          >
            {relativeDate(s.updated_at || s.created_at)}
          </span>
        )}
      </button>
      {/* (Pedro) Pin button — toggle pin/unpin. Pinnedz: sempre visível (texto
         secondary), Unpinned: opacity-0 até hover. */}
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePin(s.id);
        }}
        aria-label={pinnedNow ? `Desafixar ${s.name || ''}` : `Fixar ${s.name || ''}`}
        title={pinnedNow ? 'Desafixar do topo' : 'Fixar no topo'}
        aria-pressed={pinnedNow}
        data-testid={`sidebar-pin-${s.id}`}
        className={
          'absolute right-7 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md transition-all duration-150 focus:outline-none focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-apolo-accent/40 ' +
          (pinnedNow
            ? 'text-apolo-textSecondary opacity-100 hover:bg-apolo-surface3 hover:text-apolo-textPrimary'
            : 'text-apolo-textTertiary opacity-0 hover:bg-apolo-surface3 hover:text-apolo-textPrimary group-hover/recent:opacity-100')
        }
      >
        {pinnedNow ? (
          <PinOff size={11} aria-hidden="true" strokeWidth={2} />
        ) : (
          <Pin size={11} aria-hidden="true" strokeWidth={2} />
        )}
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => handleDeleteSession(e, s)}
        aria-label={`Excluir sessão ${s.name || ''}`}
        title="Excluir sessão"
        className="absolute right-1 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-apolo-textTertiary opacity-0 transition-all duration-150 hover:bg-apolo-surface3 hover:text-apolo-error focus:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accent/40 group-hover/recent:opacity-100"
      >
        <Trash2 size={11} aria-hidden="true" strokeWidth={2} />
      </button>
    </li>
    );
  };

  return (
    <aside
      aria-label="Sessões de código"
      data-collapsed={collapsed ? 'true' : 'false'}
      style={collapsed ? undefined : { width: sidebarWidth }}
      className={
        'group/sidebar relative z-30 flex h-full shrink-0 flex-col border-r border-apolo-borderSubtle bg-apolo-surface1 ease-out ' +
        (resizing ? '' : 'transition-[width] duration-200 ') +
        (collapsed ? 'w-14' : '')
      }
    >
      {/* Toggle collapse/expand — desktop only. Padrão Linear/Notion:
       * pill flutuante NA BORDA DIREITA da sidebar (translate-x-1/2 cruza
       * o border-r), vertical-center, hover-revealed via group/sidebar.
       * Não sobrepõe NENHUM content interno — fica metade dentro, metade
       * pro lado de fora, parecendo a alavanca natural do colapso. */}
      {isDesktop && (
        <button
          type="button"
          onClick={() => setCollapsedStored((v) => !v)}
          aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
          title={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
          className="absolute right-0 top-5 z-30 inline-flex h-6 w-6 translate-x-1/2 items-center justify-center rounded-full border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textTertiary opacity-60 shadow-sm transition hover:border-apolo-accent/50 hover:bg-apolo-surface3 hover:text-apolo-textPrimary hover:opacity-100 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-apolo-accent/50 group-hover/sidebar:opacity-100"
        >
          {collapsed ? <ChevronRight size={12} aria-hidden="true" /> : <ChevronLeft size={12} aria-hidden="true" />}
        </button>
      )}

      {/* Handle de resize — arraste a borda direita p/ aumentar/diminuir (desktop,
       * expandido). Duplo-clique reseta pra largura padrão. Linha do accent no hover. */}
      {isDesktop && !collapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Redimensionar sidebar"
          title="Arraste para redimensionar · duplo-clique reseta"
          onPointerDown={startResize}
          onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT)}
          className="group/resize absolute inset-y-0 right-0 z-20 w-2 translate-x-1/2 cursor-col-resize select-none"
        >
          <div className="absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 bg-transparent transition group-hover/resize:bg-apolo-accent/60" />
        </div>
      )}

      <header
        className={
          'flex flex-col gap-3 border-b border-apolo-borderSubtle ' +
          (collapsed ? 'px-2 py-3' : 'px-4 py-4')
        }
      >
        {collapsed ? (
          // Modo collapsed — só botão + grande
          <button
            type="button"
            onClick={onNewSession}
              aria-label="Novo chat"
            title="Novo chat"
            className={
              'inline-flex h-10 w-10 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textPrimary transition hover:bg-apolo-surface3 hover:border-apolo-borderStrong focus:outline-none focus:ring-2 focus:ring-apolo-borderStrong' +
              styles.cta
            }
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        ) : (
          <>
            <label className="relative block">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-apolo-textTertiary"
                aria-hidden="true"
              />
              <input
                type="search"
                placeholder="Buscar chats…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Buscar chats"
                className={
                  'w-full rounded-md border border-apolo-border bg-apolo-surface2 py-1.5 pl-8 pr-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary transition focus:border-apolo-accent focus:outline-none ' +
                  styles.search
                }
              />
            </label>
            <button
              type="button"
              onClick={onNewSession}
              title={`Novo chat (${modKey()}N)`}
              className={
                'inline-flex items-center justify-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-1.5 text-sm font-medium text-apolo-textPrimary transition hover:bg-apolo-surface3 hover:border-apolo-borderStrong focus:outline-none focus:ring-2 focus:ring-apolo-borderStrong ' +
                styles.cta
              }
            >
              <Plus size={14} aria-hidden="true" />
              Novo chat
              <span aria-hidden="true" className="rx-kbd rx-kbd--lg ml-1 opacity-80">{modKey()}N</span>
            </button>
          </>
        )}
      </header>

      {/* Botão "Design" (painel de preview) movido pro header do chat
         (SessionWorkspace, canto superior direito). */}

      <nav
        aria-label="Atalhos"
        className={collapsed
          ? 'flex flex-col items-center gap-1.5 px-2 py-3'
          // Ícones-quadrado juntos e alinhados À ESQUERDA (justify-start), gap fixo (8px) =
          // tight no desktop, alinhado com o conteúdo (PASTAS/RECENTS) abaixo. flex-wrap
          // preserva a responsividade no mobile (envolve pra 2ª linha se faltar largura).
          : 'flex flex-wrap items-center justify-start gap-2 px-2 py-2.5'}
      >
        <SidebarNavItem
          collapsed={collapsed}
          accent="var(--apolo-accent)"
          index={0}
          onClick={() => {
            setActive(null);
            window.dispatchEvent(new CustomEvent('apolo:setCodeView', { detail: { view: 'home' } }));
          }}
          icon={Home}
          label="Home"
          active={activeId == null && codeView === 'home'}
          title="Home — volta pra tela inicial"
        />
        <SidebarNavItem
          collapsed={collapsed}
          accent="var(--apolo-success)"
          index={1}
          onClick={() =>
            window.dispatchEvent(new CustomEvent('apolo:setCodeView', { detail: { view: 'schedules' } }))
          }
          icon={CalendarClock}
          label="Agenda"
          active={activeId == null && codeView === 'schedules'}
          title="Agenda — o que está programado"
          chevron
        />
        {/* (Pedro) Plugins & Skills + Mapa do Código nav items removed. Plugins
            stays reachable via its shortcut (openPlugins); the Code Map view is
            retired from the nav. */}
        {/* Sprint v11 — Sprints nav item between Plugins and Integrações */}
        <SidebarNavItem
          collapsed={collapsed}
          accent="var(--apolo-highlight)"
          index={3}
          onClick={() =>
            window.dispatchEvent(new CustomEvent('apolo:setCodeView', { detail: { view: 'sprints' } }))
          }
          icon={Columns3}
          label="Kanban"
          active={activeId == null && codeView === 'sprints'}
          title="Kanban — gerenciamento de demandas"
          chevron
        />
        <SidebarNavItem
          collapsed={collapsed}
          accent="var(--apolo-accent-cyan)"
          index={4}
          onClick={() => setIntegrationsOpen(true)}
          icon={PlugZap}
          label="Integrações"
          title="Integrações — Linear, Notion, Slack, Jira"
        />
      {/* Sprint 2026-06-02 — Painel (Command Center) movido do header de
       * Recents pra cá; abre o board macro READ-ONLY de todas as sessões.
       * Continua acessível via ⌘⇧O. O antigo "Mais" (placeholder disabled)
       * foi removido. */}
      <SidebarNavItem
        collapsed={collapsed}
        accent="var(--apolo-accent-pink)"
        index={6}
        onClick={() => setShowCommandCenter(true)}
        icon={LayoutDashboard}
        label="Painel"
        title={`Command Center — visão de todas as sessões (${modKey()}⇧O)`}
        chevron
      />
      </nav>

      {/* Body — Pastas + Recents só renderizam quando expanded. Collapsed mostra
       * apenas nav icons (acima) + footer (UserStatusBar global). */}
      <div className={'no-scrollbar flex-1 overflow-y-auto px-2 pb-2 ' + (collapsed ? 'hidden' : '')}>
        {/* (Pedro) "PRECISA DE VOCÊ" attention strip removed. */}
        {/* Pinned — usa renderRow (não mais SessionList) pra cada linha ganhar o
         * botão de desafixar E ficar arrastável. Arrastar uma sessão pra fora
         * (soltar no Recents) desafixa; soltar uma sessão do Recents aqui fixa. */}
        {pinned.length > 0 && (
          <section
            aria-labelledby="sidebar-section-pinned"
            className="mt-3"
            onDragOver={(e) => {
              if (!isSessionDragEvent(e)) return;
              e.preventDefault();
              if (!pinnedDragOver) setPinnedDragOver(true);
            }}
            onDragLeave={() => setPinnedDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setPinnedDragOver(false);
              const sid = readSessionIdFromDataTransfer(e.dataTransfer);
              if (sid && !isPinned(sid)) togglePin(sid); // soltar no Pinned → fixa
            }}
          >
            <h3
              id="sidebar-section-pinned"
              className={
                'flex items-center gap-1.5 px-2 pb-1 text-xs font-medium uppercase tracking-wide ' +
                (pinnedDragOver ? 'text-apolo-accent' : 'text-apolo-textTertiary')
              }
            >
              <Pin size={11} aria-hidden="true" /> Pinned
            </h3>
            <ul className={'flex flex-col gap-0.5 rounded-md transition ' + (pinnedDragOver ? 'ring-1 ring-apolo-accent/40' : '')}>
              {pinned.map(renderRow)}
            </ul>
          </section>
        )}

        {/* Hotfix v1.6.0: user-defined folders with drag-drop session ownership */}
        <section aria-label="Pastas" className="mt-3">
          <header className="flex items-center justify-between px-2 pb-1">
            <h3 className="text-xs font-medium uppercase tracking-wide text-apolo-textTertiary">
              Pastas
            </h3>
            <button
              type="button"
              onClick={() => setNewFolderMode(true)}
              aria-label="Nova pasta"
              title="Nova pasta"
              className="inline-flex h-5 w-5 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accent/40"
            >
              <FolderPlus size={12} aria-hidden="true" />
            </button>
          </header>
          <div className="flex flex-col gap-0.5">
            {newFolderMode && (
              <div className="flex items-center gap-1.5 rounded-md bg-apolo-surface2 px-1.5 py-1.5">
                <Folder size={12} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
                <input
                  ref={newFolderInputRef}
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onBlur={commitNewFolder}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitNewFolder();
                    else if (e.key === 'Escape') {
                      setNewFolderName('');
                      setNewFolderMode(false);
                    }
                  }}
                  placeholder="Nome da pasta…"
                  aria-label="Nome da nova pasta"
                  className="flex-1 min-w-0 rounded border border-apolo-borderFocus bg-apolo-surface1 px-1.5 py-0.5 text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
                />
              </div>
            )}
            {folders.length === 0 && !newFolderMode && (
              <button
                type="button"
                onClick={() => setNewFolderMode(true)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textSecondary"
              >
                <FolderPlus size={11} aria-hidden="true" />
                Criar primeira pasta
              </button>
            )}
            {folders.map((f) => (
              <FolderRow
                key={f.id}
                folder={f}
                sessions={(sessionsByFolder.get(f.id) || []).filter((s) => !s.pinned)}
                activeId={activeId}
                onPickSession={setActive}
              />
            ))}
          </div>
        </section>

        {/* Recents — sessions without a folder. Drag-target for "ungroup". */}
        <section
          aria-label="Recents"
          className="mt-4"
          onDragOver={(e) => {
            if (!isSessionDragEvent(e)) return;
            e.preventDefault();
            if (!recentsDragOver) setRecentsDragOver(true);
          }}
          onDragLeave={() => setRecentsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setRecentsDragOver(false);
            const sid = readSessionIdFromDataTransfer(e.dataTransfer);
            if (!sid) return;
            if (isPinned(sid)) unpin(sid); // arrastou pra fora do Pinned → desafixa
            moveSession(sid, null); // e garante raiz (sem pasta)
          }}
        >
          <div className="flex items-center justify-between px-2 pb-1">
            <h3 className={'text-xs font-medium uppercase tracking-wide ' + (recentsDragOver ? 'text-apolo-accent' : 'text-apolo-textTertiary')}>
              Recents
            </h3>
            {/* Sprint 2026-06-02 — botão "Painel" movido pro nav (abaixo de
             * Integrações). Aqui sobra só o toggle Lista/Board. */}
            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={organizeNames}
                disabled={backfilling}
                aria-label="Organizar nomes das sessões"
                title="Organizar nomes — renomeia as sessões genéricas pela 1ª mensagem"
                className={'inline-flex h-5 w-5 items-center justify-center rounded text-apolo-textTertiary transition hover:text-apolo-textPrimary disabled:opacity-40 ' + (backfilling ? 'animate-pulse' : '')}
              >
                <Wand2 size={12} aria-hidden="true" />
              </button>
              <div className="inline-flex items-center gap-0.5" role="group" aria-label="Visão das sessões">
                <button
                  type="button"
                  onClick={() => setUiPref('sessionView', 'list')}
                  aria-pressed={sessionView === 'list'}
                  title="Lista"
                  className={'inline-flex h-5 w-5 items-center justify-center rounded ' + (sessionView === 'list' ? 'bg-apolo-surface3 text-apolo-textPrimary' : 'text-apolo-textTertiary hover:text-apolo-textPrimary')}
                >
                  <ListIcon size={12} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setUiPref('sessionView', 'project')}
                  aria-pressed={sessionView === 'project'}
                  title="Agrupar por projeto"
                  className={'inline-flex h-5 w-5 items-center justify-center rounded ' + (sessionView === 'project' ? 'bg-apolo-surface3 text-apolo-textPrimary' : 'text-apolo-textTertiary hover:text-apolo-textPrimary')}
                >
                  <Folder size={12} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setUiPref('sessionView', 'time')}
                  aria-pressed={sessionView === 'time'}
                  title="Agrupar por tempo"
                  className={'inline-flex h-5 w-5 items-center justify-center rounded ' + (sessionView === 'time' ? 'bg-apolo-surface3 text-apolo-textPrimary' : 'text-apolo-textTertiary hover:text-apolo-textPrimary')}
                >
                  <CalendarClock size={12} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setUiPref('sessionView', 'board')}
                  aria-pressed={sessionView === 'board'}
                  title="Board por status"
                  className={'inline-flex h-5 w-5 items-center justify-center rounded ' + (sessionView === 'board' ? 'bg-apolo-surface3 text-apolo-textPrimary' : 'text-apolo-textTertiary hover:text-apolo-textPrimary')}
                >
                  <LayoutGrid size={12} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {sessionView === 'project' ? (
            projectGroups.length === 0 ? (
              <p className="px-2 py-1 text-[10px] text-apolo-textTertiary">Nenhuma sessão neste filtro.</p>
            ) : (
              projectGroups.map((g) => (
                <div key={g.project} className="mb-1.5">
                  <div className="flex items-center gap-1.5 px-2 pb-0.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-sm"
                      style={g.project === 'Sem projeto'
                        ? { background: 'currentColor', opacity: 0.35 }
                        : { background: pluginColor(g.project) }}
                    />
                    <span className="min-w-0 truncate text-[10px] font-semibold uppercase tracking-wide text-apolo-textTertiary">{g.project}</span>
                    <span className="ml-auto text-[10px] text-apolo-textTertiary">{g.sessions.length}</span>
                  </div>
                  <ul className="flex flex-col gap-0.5">{g.sessions.map(renderRow)}</ul>
                </div>
              ))
            )
          ) : sessionView === 'time' ? (
            timeGroups.length === 0 ? (
              <p className="px-2 py-1 text-[10px] text-apolo-textTertiary">Nenhuma sessão neste filtro.</p>
            ) : (
              timeGroups.map((g) => (
                <div key={g.key} className="mb-1.5">
                  <div className="flex items-center gap-1.5 px-2 pb-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-apolo-textTertiary">{g.label}</span>
                    <span className="ml-auto text-[10px] text-apolo-textTertiary">{g.sessions.length}</span>
                  </div>
                  <ul className="flex flex-col gap-0.5">{g.sessions.map(renderRow)}</ul>
                </div>
              ))
            )
          ) : sessionView === 'board' ? (
            recentGroups.length === 0 ? (
              <p className="px-2 py-1 text-[10px] text-apolo-textTertiary">Nenhuma sessão neste filtro.</p>
            ) : (
              recentGroups.map((g) => (
                <div key={g.status} className="mb-1.5">
                  <div className="flex items-center gap-1.5 px-2 pb-0.5">
                    <SessionStatusBadge status={g.status} compact />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-apolo-textTertiary">
                      {sessionStatusMeta(g.status).label}
                    </span>
                    <span className="ml-auto text-[10px] text-apolo-textTertiary">{g.sessions.length}</span>
                  </div>
                  <ul className="flex flex-col gap-0.5">{g.sessions.map(renderRow)}</ul>
                </div>
              ))
            )
          ) : (
            <ul className={'flex flex-col gap-0.5 rounded-md transition ' + (recentsDragOver ? 'ring-1 ring-apolo-accent/40' : '')}>
              {visibleUngrouped.map(renderRow)}
              {visibleUngrouped.length === 0 && ungrouped.length > 0 && (
                <li className="px-2 py-1 text-[10px] text-apolo-textTertiary">Nenhuma sessão neste filtro.</li>
              )}
              {ungrouped.length === 0 && folders.length > 0 && (
                <li className="px-2 py-1 text-[10px] text-apolo-textTertiary">
                  Todas as sessões estão organizadas em pastas.
                </li>
              )}
            </ul>
          )}
        </section>

        {sessions.length === 0 && (
          <p className="px-2 pt-4 text-xs text-apolo-textTertiary">
            Nenhum chat ainda. Use <span className="text-apolo-textSecondary">Novo chat</span> para começar.
          </p>
        )}
      </div>

      {/* Sprint v9.14 — footer removido: user-menu agora é UserStatusBar
       * flutuante global no AppShell (visível em todas as tabs). Sem
       * duplicação. */}

      {/* Sprint v6 B3: Schedule modal opened via Schedules button or ⇧⌘T. */}
      <ScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} />
      {/* Sprint v5 A5+A6: Plugins & Skills modal with builtin catalog + .zip upload. */}
      <PluginsView open={pluginsOpen} onClose={() => setPluginsOpen(false)} />
      {/* Sprint v9.6 — Integrações modal (Linear ativo, demais coming-soon). */}
      <IntegrationsView open={integrationsOpen} onClose={() => setIntegrationsOpen(false)} />
      {/* Command Center — board macro READ-ONLY de todas as sessões (⌘⇧O). */}
      <CommandCenterModal open={showCommandCenter} onClose={() => setShowCommandCenter(false)} />
    </aside>
  );
}
